import { Component, ElementRef, ViewChild, inject } from "@angular/core";
import { Pages } from "@constants/pages/Pages";
import { SeriesBarOptions } from "highcharts";
import { BaseHighchartWidget } from "../BaseHighchartWidget";
import { IncidentService } from "@app/incident/services/incident.service";
import { IncidentStatus } from "@app/incident/models/incident-status.enum";
import { TabEventListenerType } from "@enums/TabEventListenerType.enum";
import { firstValueFrom } from "rxjs";
import { provideTranslocoScope } from "@jsverse/transloco";

@Component({
    selector: "incident-widget",
    templateUrl: "../highchart-widget.component.html",
    styleUrls: ["../highchart-widget.component.scss"],
    providers: [provideTranslocoScope("dashboard")]
})
export class IncidentWidgetComponent extends BaseHighchartWidget {
    @ViewChild("chartContainer") chartContainer: ElementRef;
    filters: any;

    private incidentDataService = inject(IncidentService);
    private pages = inject(Pages);

    protected async getChartConfig() {
        await this.preloadDashboardScope();

        const filters = this.getFilters();
        this.filters = filters;
        const data = await firstValueFrom(this.incidentDataService.getWidgetInfo(filters));

        let optionsData = [];
        let optionscategories = [];

        this.config.noData = !data || data.totalCount === 0;
        if (this.config.noData) {
            return null;
        }

        if (this.filters.FilterIncidentStatus.length == 0) {
            optionsData = [
                {
                    name: "Incidents waiting for acceptance",
                    y: data.waitingForAcceptanceCount,
                    color: "#024d60",
                },
                {
                    name: "Incidents waiting for improvements",
                    y: data.waitingForImprovementCount,
                    color: "#c4b80a",
                },
                {
                    name: "Incidents ready for review",
                    y: data.readyForReviewCount,
                    color: "#ff9900",
                },
                {
                    name: "Incidents rejected and closed",
                    y: data.rejectedClosedCount,
                    color: "#d65035",
                },
                {
                    name: "Incidents approved and closed",
                    y: data.approvedClosedCount,
                    color: "#097F1E",
                },
            ];
            optionscategories = [
                "Incidents waiting for acceptance",
                "Incidents waiting for improvements",
                "Incidents ready for review",
                "Incidents rejected and closed",
                "Incidents approved and closed",
            ];
        } else {
            this.filters.FilterIncidentStatus.forEach((status) => {
                switch (status) {
                    case IncidentStatus.AwaitingAcceptance:
                        optionsData.push({
                            name: "Incidents waiting for acceptance",
                            y: data.waitingForAcceptanceCount,
                            color: "#024d60",
                        });
                        optionscategories.push("Incidents waiting for acceptance");
                        break;
                    case IncidentStatus.AwaitingImprovements:
                        optionsData.push({
                            name: "Incidents waiting for improvements",
                            y: data.waitingForImprovementCount,
                            color: "#c4b80a",
                        });
                        optionscategories.push("Incidents waiting for improvements");
                        break;
                    case IncidentStatus.ReadyForApproval:
                        optionsData.push({
                            name: "Incidents ready for review",
                            y: data.readyForReviewCount,
                            color: "#ff9900",
                        });
                        optionscategories.push("Incidents ready for review");
                        break;
                    case IncidentStatus.Rejected:
                        optionsData.push({
                            name: "Incidents rejected and closed",
                            y: data.rejectedClosedCount,
                            color: "#d65035",
                        });
                        optionscategories.push("Incidents rejected and closed");
                        break;
                    case IncidentStatus.Closed:
                        optionsData.push({
                            name: "Incidents approved and closed",
                            y: data.approvedClosedCount,
                            color: "#097F1E",
                        });
                        optionscategories.push("Incidents approved and closed");
                        break;
                }
            });
        }

        const mappedData = [
            <SeriesBarOptions>{
                pointWidth: 25,
                name: this.transloco.translate("dashboard.incidentWidget.seriesName"),
                data: optionsData,
            },
        ];

        const config = this.getBaseChartConfig(null, mappedData);

        config.legend = {
            enabled: false,
        };

        config.tooltip = {
            headerFormat: "<b>{point.x}:</b> {point.y}",
            pointFormat: "",
        };

        config.xAxis = {
            labels: {
                format: "{value:.0f}",
            },
            categories: optionscategories,
        };

        config.yAxis = {
            min: 0,
            allowDecimals: false,
            title: {
                text: this.transloco.translate("dashboard.incidentWidget.yAxisTitle"),
            },
        };

        return config;
    }

    protected async setStatusFilter(category: string) {
        switch (category) {
            case "Incidents waiting for acceptance":
                this.filters.IncidentStatus = IncidentStatus.AwaitingAcceptance;
                break;
            case "Incidents waiting for improvements":
                this.filters.IncidentStatus = IncidentStatus.AwaitingImprovements;
                break;
            case "Incidents ready for review":
                this.filters.IncidentStatus = IncidentStatus.ReadyForApproval;
                break;
            case "Incidents rejected and closed":
                this.filters.IncidentStatus = IncidentStatus.Rejected;
                break;
            case "Incidents approved and closed":
                this.filters.IncidentStatus = IncidentStatus.Closed;
                break;
        }
    }
    protected async openWorkspace(ev) {
        this.setStatusFilter(ev.point.name);

        this._tabService.generateTab(this.pages.IncidentOverview, null, "", {
            navFromWidget: true,
            filters: this.filters,
        });

        this._tabService.listeners.triggerGlobalListener(
            TabEventListenerType.LoadPreset,
            this.pages.IncidentOverview,
            {
                navFromWidget: true,
                filters: this.filters,
            }
        );
    }

    protected async onGlobalOpenClick() {
        const preset = await this.getBasePreset();
        this.openPreset(this.pages.IncidentOverview, preset);
    }
}
